import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { AppContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { 
  FaArrowLeft, FaMapMarkerAlt, FaCheckCircle, FaTimesCircle,
  FaClock, FaTruck, FaShieldAlt, FaComments, FaCalendar
} from 'react-icons/fa';

export default function RentalDetail() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const { API, user } = useContext(AppContext);
  const navigate = useNavigate();
  
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [rentalForm, setRentalForm] = useState({
    start_date: '',
    end_date: '',
    user_name: user?.name || '',
    user_email: user?.email || '',
    user_phone: '',
    notes: ''
  });

  useEffect(() => {
    fetchProduct();
  }, [id]);

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API}/products/${id}`);
      setProduct(response.data);
    } catch (error) {
      console.error('Error fetching product:', error);
      toast.error('Produit non trouvé');
      navigate('/rental');
    } finally {
      setLoading(false);
    }
  };

  const calculateTotal = () => {
    if (!rentalForm.start_date || !rentalForm.end_date || !product) return 0;
    
    const start = new Date(rentalForm.start_date);
    const end = new Date(rentalForm.end_date);
    const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
    
    if (days <= 0) return 0;
    
    // Degressive pricing (example logic)
    let pricePerDay = product.price_rental;
    if (days >= 30) {
      pricePerDay = pricePerDay * 0.7; // 30% discount for monthly
    } else if (days >= 7) {
      pricePerDay = pricePerDay * 0.85; // 15% discount for weekly
    }
    
    return (days * pricePerDay).toFixed(2);
  };

  const handleStartConversation = async () => {
    if (!user) {
      toast.error('Veuillez vous connecter pour contacter le loueur');
      navigate('/login');
      return;
    }

    try {
      // Get owner ID from product
      const ownerId = product.created_by_user_id || product.shop_id;
      if (!ownerId) {
        toast.error('Impossible de trouver le propriétaire de cet équipement');
        return;
      }

      // Create or get existing conversation
      const response = await axios.post(`${API}/rentals/conversations`, {
        product_id: product.id,
        renter_id: user.id,
        owner_id: ownerId,
        initial_message: `Bonjour, je suis intéressé par la location de ${i18n.language === 'fr' ? product.name_fr : product.name_en}.`
      });

      toast.success('Conversation créée');
      navigate(`/rentals/conversations/${response.data.id}`);
    } catch (error) {
      console.error('Error starting conversation:', error);
      toast.error('Erreur lors de la création de la conversation');
    }
  };

  const handleRentalRequest = async (e) => {
    e.preventDefault();
    
    if (!user) {
      toast.error('Veuillez vous connecter pour faire une demande de location');
      navigate('/login');
      return;
    }

    try {
      await axios.post(`${API}/rentals`, {
        user_id: user.id,
        user_email: rentalForm.user_email,
        user_name: rentalForm.user_name,
        user_phone: rentalForm.user_phone,
        product_id: product.id,
        start_date: rentalForm.start_date,
        end_date: rentalForm.end_date,
        total: parseFloat(calculateTotal()),
        notes: rentalForm.notes
      });

      toast.success(t('rental.rentalSuccess'));
      setRentalForm({
        ...rentalForm,
        start_date: '',
        end_date: '',
        notes: ''
      });
    } catch (error) {
      console.error('Error creating rental:', error);
      toast.error('Erreur lors de la demande de location');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">
        <div className="text-xl text-[#0A2540]">{t('loading')}</div>
      </div>
    );
  }

  if (!product) return null;

  const name = i18n.language === 'fr' ? product.name_fr : product.name_en;
  const description = i18n.language === 'fr' ? product.description_fr : product.description_en;

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <div className="container mx-auto px-4 py-8">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate('/rental')}
          className="mb-6 text-[#0A2540]"
        >
          <FaArrowLeft className="mr-2" /> Retour au catalogue
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column - Images */}
          <div>
            <Card>
              <CardContent className="p-4">
                {/* Main Image */}
                <div className="mb-4">
                  <img
                    src={product.images && product.images.length > 0 ? product.images[selectedImage] : '/placeholder-equipment.jpg'}
                    alt={name}
                    className="w-full h-96 object-cover rounded-lg"
                  />
                </div>

                {/* Thumbnail Gallery */}
                {product.images && product.images.length > 1 && (
                  <div className="grid grid-cols-4 gap-2">
                    {product.images.map((img, idx) => (
                      <img
                        key={idx}
                        src={img}
                        alt={`${name} ${idx + 1}`}
                        className={`w-full h-20 object-cover rounded cursor-pointer border-2 ${
                          selectedImage === idx ? 'border-[#00D4FF]' : 'border-gray-300'
                        }`}
                        onClick={() => setSelectedImage(idx)}
                      />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Technical Specs */}
            <Card className="mt-4">
              <CardHeader>
                <CardTitle className="text-[#0A2540]">{t('rental.technicalSpecs')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between border-b pb-2">
                    <span className="font-semibold">Catégorie:</span>
                    <span>{product.category_id}</span>
                  </div>
                  {product.location && (
                    <div className="flex justify-between border-b pb-2">
                      <span className="font-semibold">Localisation:</span>
                      <span className="flex items-center gap-1">
                        <FaMapMarkerAlt className="text-[#00D4FF]" />
                        {product.location}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between border-b pb-2">
                    <span className="font-semibold">Stock disponible:</span>
                    <span>{product.stock_rental}</span>
                  </div>
                  {product.delivery_time && (
                    <div className="flex justify-between border-b pb-2">
                      <span className="font-semibold">Délai de livraison:</span>
                      <span className="flex items-center gap-1">
                        <FaClock className="text-[#0A2540]" />
                        {product.delivery_time}
                      </span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Details & Rental Form */}
          <div>
            {/* Product Info */}
            <Card className="mb-4">
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h1 className="text-3xl font-bold text-[#0A2540] mb-2">{name}</h1>
                    {product.stock_rental > 0 ? (
                      <Badge className="bg-[#635BFF] text-white">
                        <FaCheckCircle className="mr-1" /> Disponible
                      </Badge>
                    ) : (
                      <Badge className="bg-red-500 text-white">
                        <FaTimesCircle className="mr-1" /> Indisponible
                      </Badge>
                    )}
                  </div>
                  <div className="text-right">
                    {product.show_price !== false ? (
                      <>
                        <div className="text-4xl font-bold text-[#00D4FF]">{product.price_rental}€</div>
                        <div className="text-sm text-gray-500">{t('rental.perDay')}</div>
                      </>
                    ) : (
                      <div className="text-2xl font-semibold text-[#635BFF]">
                        {i18n.language === 'fr' ? 'Prix sur demande' : 'Price on request'}
                      </div>
                    )}
                  </div>
                </div>

                <p className="text-gray-700 mb-4">{description}</p>

                <Button
                  onClick={handleStartConversation}
                  className="w-full bg-[#0A2540] hover:bg-[#003A52]"
                >
                  <FaComments className="mr-2" /> {t('rental.contactOwner')}
                </Button>
              </CardContent>
            </Card>

            {/* Pricing Info */}
            {product.show_price !== false && (
            <Card className="mb-4">
              <CardHeader>
                <CardTitle className="text-[#0A2540]">{t('rental.pricing')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span>Tarif journalier:</span>
                    <span className="font-bold">{product.price_rental}€ / jour</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Tarif hebdomadaire (7+ jours):</span>
                    <span className="font-bold">{(product.price_rental * 0.85).toFixed(2)}€ / jour (-15%)</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Tarif mensuel (30+ jours):</span>
                    <span className="font-bold">{(product.price_rental * 0.7).toFixed(2)}€ / jour (-30%)</span>
                  </div>
                </div>
              </CardContent>
            </Card>
            )}

            {/* Rental Request Form */}
            <Card>
              <CardHeader>
                <CardTitle className="text-[#0A2540] flex items-center gap-2">
                  <FaCalendar /> Demande de Location
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleRentalRequest} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>{t('rental.startDate')}</Label>
                      <Input
                        type="date"
                        required
                        value={rentalForm.start_date}
                        onChange={(e) => setRentalForm({...rentalForm, start_date: e.target.value})}
                        min={new Date().toISOString().split('T')[0]}
                      />
                    </div>
                    <div>
                      <Label>{t('rental.endDate')}</Label>
                      <Input
                        type="date"
                        required
                        value={rentalForm.end_date}
                        onChange={(e) => setRentalForm({...rentalForm, end_date: e.target.value})}
                        min={rentalForm.start_date}
                      />
                    </div>
                  </div>

                  <div>
                    <Label>Nom complet</Label>
                    <Input
                      required
                      value={rentalForm.user_name}
                      onChange={(e) => setRentalForm({...rentalForm, user_name: e.target.value})}
                    />
                  </div>

                  <div>
                    <Label>Email</Label>
                    <Input
                      type="email"
                      required
                      value={rentalForm.user_email}
                      onChange={(e) => setRentalForm({...rentalForm, user_email: e.target.value})}
                    />
                  </div>

                  <div>
                    <Label>Téléphone</Label>
                    <Input
                      type="tel"
                      value={rentalForm.user_phone}
                      onChange={(e) => setRentalForm({...rentalForm, user_phone: e.target.value})}
                    />
                  </div>

                  <div>
                    <Label>Message / Besoins spécifiques</Label>
                    <Textarea
                      rows={3}
                      value={rentalForm.notes}
                      onChange={(e) => setRentalForm({...rentalForm, notes: e.target.value})}
                      placeholder="Décrivez vos besoins ou posez vos questions..."
                    />
                  </div>

                  {calculateTotal() > 0 && (
                    <div className="bg-[#F5F5F5] p-4 rounded-lg">
                      <div className="flex justify-between items-center">
                        <span className="font-semibold">Total estimé:</span>
                        <span className="text-3xl font-bold text-[#00D4FF]">{calculateTotal()}€</span>
                      </div>
                      <p className="text-sm text-gray-600 mt-2">
                        Pour {Math.ceil((new Date(rentalForm.end_date) - new Date(rentalForm.start_date)) / (1000 * 60 * 60 * 24))} jours de location
                      </p>
                    </div>
                  )}

                  <Button 
                    type="submit" 
                    className="w-full bg-[#00D4FF] hover:bg-[#00B4D8]"
                    disabled={!rentalForm.start_date || !rentalForm.end_date || product.stock_rental === 0}
                  >
                    {t('rental.rent')}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
